import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import useAuthStore from "../store/authStore";
import {
  FiSun,
  FiMoon,
  FiMenu,
  FiX,
  FiLogIn,
  FiLogOut,
  FiGrid,
  FiUser,
} from "react-icons/fi";
import "./Header.css";

function Header() {
  const currentTheme = localStorage.getItem("theme");
  if (currentTheme) {
    document.documentElement.setAttribute("data-theme", currentTheme);
  }

  const [darkMode, setDarkMode] = useState(currentTheme === "dark");
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const { email, isAuthenticated, role, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  // Track scroll for navbar background
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  function toggleTheme() {
    const next = !darkMode;
    setDarkMode(next);
    document.documentElement.setAttribute("data-theme", next ? "dark" : "light");
    localStorage.setItem("theme", next ? "dark" : "light");
  }

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <header className={`hdr ${scrolled ? "hdr--scrolled" : ""}`}>
      <div className="hdr__inner">
        {/* ── Logo ── */}
        <NavLink to="/" className="hdr__logo">
          <img
            src="/bouchaddakh-logo.png"
            alt="Bouchaddakh Logo"
            className="hdr__logo-img"
          />
        </NavLink>

        {/* ── Desktop + Mobile nav links ── */}
        <nav className={`hdr__nav ${menuOpen ? "hdr__nav--open" : ""}`}>
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `hdr__link ${isActive ? "hdr__link--active" : ""}`
            }
          >
            Accueil
          </NavLink>
          <NavLink
            to="/about"
            className={({ isActive }) =>
              `hdr__link ${isActive ? "hdr__link--active" : ""}`
            }
          >
            Qui Sommes-Nous
          </NavLink>
          <NavLink
            to="/produits"
            className={({ isActive }) =>
              `hdr__link ${isActive ? "hdr__link--active" : ""}`
            }
          >
            Nos Produits
          </NavLink>
          <NavLink
            to="/contact"
            className={({ isActive }) =>
              `hdr__link ${isActive ? "hdr__link--active" : ""}`
            }
          >
            Contact
          </NavLink>

          {/* Auth section — visible in mobile too */}
          <div className="hdr__auth hdr__auth--mobile">
            {isAuthenticated ? (
              <>
                <span className="hdr__user">
                  <FiUser size={14} /> {email}
                </span>
                {role === "admin" && (
                  <button
                    className="hdr__btn hdr__btn--outline"
                    onClick={() => navigate("/admin/dashboard")}
                  >
                    <FiGrid size={14} /> Dashboard
                  </button>
                )}
                <button
                  className="hdr__btn hdr__btn--ghost"
                  onClick={handleLogout}
                >
                  <FiLogOut size={14} /> Déconnexion
                </button>
              </>
            ) : (
              <NavLink to="/login" className="hdr__btn hdr__btn--primary">
                <FiLogIn size={14} /> Connexion
              </NavLink>
            )}
          </div>
        </nav>

        {/* ── Right side (desktop auth + theme + hamburger) ── */}
        <div className="hdr__actions">
          {/* Auth buttons — desktop only */}
          <div className="hdr__auth hdr__auth--desktop">
            {isAuthenticated ? (
              <>
                <span className="hdr__user">
                  <FiUser size={14} /> {email}
                </span>
                {role === "admin" && (
                  <button
                    className="hdr__btn hdr__btn--outline"
                    onClick={() => navigate("/admin/dashboard")}
                  >
                    <FiGrid size={14} /> Dashboard
                  </button>
                )}
                <button
                  className="hdr__btn hdr__btn--ghost"
                  onClick={handleLogout}
                >
                  <FiLogOut size={14} /> Déconnexion
                </button>
              </>
            ) : (
              <NavLink to="/login" className="hdr__btn hdr__btn--primary">
                <FiLogIn size={14} /> Connexion
              </NavLink>
            )}
          </div>

          {/* Theme toggle */}
          <button
            className="hdr__theme-toggle"
            onClick={toggleTheme}
            aria-label="Toggle theme"
          >
            {darkMode ? <FiSun size={20} /> : <FiMoon size={20} />}
          </button>

          {/* Hamburger */}
          <button
            className="hdr__hamburger"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
        </div>
      </div>

      {/* Overlay when mobile menu is open */}
      {menuOpen && (
        <div className="hdr__overlay" onClick={() => setMenuOpen(false)} />
      )}
    </header>
  );
}

export default Header;
